'use client'

import { cn } from '@/lib/utils'
import { DetailText, HeadlineText } from 'design-configuration/components'
import { AllPurposeLink } from '@components/all-purpose-link'
import { articleRoute, artistRoute, exhibitionShowRoute, projectShowRoute } from '@utils/routes'
import { BentoItemFragment, Enum_Componenthomebentoitem_Type, Enum_Exhibition_Type } from '@/gql/generated/graphql'
import { ImageWithCustomLoader } from '@components/image-with-custom-loader'
import { useEffect, useRef, useState } from 'react'
import { trackButtonClicked, TrackedButtonNames } from '@utils/analytics'

interface BentoItemProps {
  bentoItem: BentoItemFragment
  bentoBoxName: string
  bentoBoxPosition: number
}

const bentoItemLink = (bentoItem: BentoItemFragment): string => {
  if (bentoItem.Link) {
    return bentoItem.Link
  }
  switch (bentoItem.Type) {
    case Enum_Componenthomebentoitem_Type.Article:
      return articleRoute(bentoItem.article?.data?.attributes?.slug ?? '')
    case Enum_Componenthomebentoitem_Type.Project:
      return projectShowRoute(bentoItem.project?.Project?.slug ?? '')
    case Enum_Componenthomebentoitem_Type.Exhibition:
      return exhibitionShowRoute(bentoItem.exhibition?.data?.attributes?.slug ?? '')
    case Enum_Componenthomebentoitem_Type.ArtistProfile:
      return artistRoute(bentoItem.artist_editorial_page?.data?.attributes?.slug ?? '')
    default:
      return ''
  }
}

const bentoItemImage = (bentoItem: BentoItemFragment): string => {
  if (bentoItem.ThumbnailMedia?.data?.attributes?.url) {
    return bentoItem.ThumbnailMedia.data.attributes.url
  }
  switch (bentoItem.Type) {
    case Enum_Componenthomebentoitem_Type.Article:
      return (
        bentoItem.article?.data?.attributes?.coverPhoto?.data?.attributes?.url ??
        bentoItem.article?.data?.attributes?.thumbnail?.data?.attributes?.url ??
        ''
      )
    case Enum_Componenthomebentoitem_Type.Project:
      return bentoItem.project?.Project?.featured_token?.[0]?.media_url ?? ''
    case Enum_Componenthomebentoitem_Type.Exhibition:
      return bentoItem.exhibition?.data?.attributes?.HeaderImage?.data?.attributes?.url ?? ''
    case Enum_Componenthomebentoitem_Type.ArtistProfile:
      return bentoItem.artist_editorial_page?.data?.attributes?.headerImage?.data?.attributes?.url ?? ''
    default:
      return ''
  }
}

const bentoItemTitle = (bentoItem: BentoItemFragment): string => {
  if (bentoItem.Title) {
    return bentoItem.Title
  }
  switch (bentoItem.Type) {
    case Enum_Componenthomebentoitem_Type.Article:
      return bentoItem.article?.data?.attributes?.title ?? ''
    case Enum_Componenthomebentoitem_Type.Project:
      return bentoItem.project?.Project?.name ?? ''
    case Enum_Componenthomebentoitem_Type.Exhibition:
      return bentoItem.exhibition?.data?.attributes?.Title ?? ''
    case Enum_Componenthomebentoitem_Type.ArtistProfile:
      return bentoItem.artist_editorial_page?.data?.attributes?.artistName ?? ''
    default:
      return ''
  }
}

const bentoItemOverlineText = (bentoItem: BentoItemFragment): string => {
  if (bentoItem.OverlineText) {
    return bentoItem.OverlineText
  }
  switch (bentoItem.Type) {
    case Enum_Componenthomebentoitem_Type.Article:
      return 'From the Journal'
    case Enum_Componenthomebentoitem_Type.Project:
      return 'The Art Blocks Collection'
    case Enum_Componenthomebentoitem_Type.Exhibition:
      return 'Exhibition Spotlight'
    case Enum_Componenthomebentoitem_Type.ArtistProfile:
      return 'Artist Spotlight'
    default:
      return ''
  }
}

const bentoItemSubheadingText = (bentoItem: BentoItemFragment): string => {
  const exhibition = bentoItem.exhibition?.data?.attributes ?? undefined
  switch (bentoItem.Type) {
    case Enum_Componenthomebentoitem_Type.Project:
      return bentoItem.project?.Project?.artist_name ?? ''
    case Enum_Componenthomebentoitem_Type.Exhibition:
      if (!exhibition) {
        return ''
      }
      return (
        (exhibition.Type === Enum_Exhibition_Type.Group ? exhibition.Collections : exhibition.Collections.slice(0, 1))
          .map((project) => project?.Collection?.Project?.artist_name)
          .join(', ') ?? ''
      )
    default:
      return ''
  }
}

export function BentoItem({ bentoItem, bentoBoxName, bentoBoxPosition }: BentoItemProps): React.ReactNode {
  const [isHovered, setIsHovered] = useState(false)
  const itemLink = bentoItemLink(bentoItem)
  const mediaUrl = bentoItemImage(bentoItem)
  const title = bentoItemTitle(bentoItem)
  const overlineText = bentoItemOverlineText(bentoItem)
  const subheadingText = bentoItemSubheadingText(bentoItem)

  const isVideo = mediaUrl?.includes('.mp4')
  return (
    <AllPurposeLink
      href={itemLink}
      className="rounded-xs relative block h-full w-full overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => {
        trackButtonClicked(TrackedButtonNames.bentoItem, {
          customProperties: {
            'Bento item': title,
            'Bento item overline': overlineText,
            'Bento item type': bentoItem.Type,
            'Bento item link': itemLink,
            'Bento box': bentoBoxName,
            'Bento box position': bentoBoxPosition
          },
          project: bentoItem.project?.Project ?? undefined,
          exhibition: bentoItem.exhibition?.data?.attributes ?? undefined,
          article: bentoItem.article?.data?.attributes ?? undefined,
          artistPage: bentoItem.artist_editorial_page?.data?.attributes ?? undefined
        })
      }}
    >
      <div className={cn('absolute inset-0')}>
        {isVideo ? (
          <BentoVideo
            src={mediaUrl}
            className="h-full w-full"
            videoStyle="object-cover object-center"
            isHovered={isHovered}
            muted={true}
            controls={false}
          />
        ) : (
          mediaUrl && (
            <ImageWithCustomLoader
              src={mediaUrl}
              alt={title || 'Bento item'}
              fill
              className={cn('object-cover transition-transform duration-300', isHovered && 'scale-105')}
              sizes="50vw"
            />
          )
        )}

        {/* Overlay gradient */}
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(85.77% 70.71% at 50% 50%, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.70) 100%)',
            boxShadow: '3px 3px 6px 0px rgba(0, 0, 0, 0.20)'
          }}
        />
      </div>

      {/* Project info */}
      <div className={cn('p-md text-on-surface-inverted absolute left-0 top-0 w-full')}>
        <DetailText className="font-medium uppercase">{overlineText}</DetailText>
        <HeadlineText className="font-medium">{title}</HeadlineText>
        <HeadlineText className="font-light">{subheadingText}</HeadlineText>
      </div>
    </AllPurposeLink>
  )
}

interface BentoVideoProps {
  src: string
  className?: string
  videoStyle?: string
  isHovered?: boolean
  muted?: boolean
  controls?: boolean
}

export function BentoVideo({
  src,
  className,
  videoStyle,
  isHovered = false,
  muted = true,
  controls = false
}: BentoVideoProps): React.ReactElement | null {
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    if (!videoRef.current) return

    if (isHovered) {
      videoRef.current.play()
    } else {
      videoRef.current.pause()
    }
  }, [isHovered])

  if (!src) return null

  return (
    <video
      ref={videoRef}
      className={cn('h-full w-full', videoStyle, className)}
      preload="metadata"
      loop
      playsInline
      muted={muted}
      controls={controls}
    >
      <source src={src} />
    </video>
  )
}
